import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { captureParts, photoTypeLabel } from '../utils/photoCapture';
import { resolvePhotoUrl } from '../utils/photoUrl';
import { trapTab } from '../utils/focusTrap';
import { reportTab } from '../utils/issueReports';
import ZoomableImage from './ZoomableImage';
import BundleLayers from './zstack/BundleLayers';

/**
 * One photograph at full resolution, over the page: the viewer PhotoGallery
 * opens from its thumbnails, and the hub's lightbox (hub/IsolateLightbox)
 * opens from a row's picture. One viewer, so a photograph is magnified,
 * captioned and stepped through the same way wherever it is opened.
 *
 *   - ZoomableImage draws the photograph: buttons, wheel, double-click, pinch
 *     and the +/-/0 keys magnify it. Nothing here zooms on its own.
 *   - Under it, what the photograph is: its view (common:photoType), taxon,
 *     record code and reference tag -- or `renderHead`'s own first line --
 *     then its notes, when and where it was taken (PhotoCaptureLine),
 *     anything `extraCaption` adds, and a row of the counter, the annotate
 *     button, `extraActions` and a link to the file itself.
 *   - Previous/next and the arrow keys step through `photos`, wrapping
 *     around; Escape, the × and the backdrop close.
 *   - A modal dialog: focus moves into it as it opens, Tab stays inside, and
 *     focus goes back to `returnFocusTo` (else whatever had it) on close.
 *   - A layer of a Z-stack (`bundle_id`, 0095) carries the stack's badge and a
 *     slider through its layers (zstack/BundleLayers.js): the layer chosen is
 *     drawn instead. The arrow keys move the slider while it has the focus,
 *     not the photograph.
 *   - `strip`: a row of every photograph's small copy under the caption, the
 *     open one marked (aria-current) and kept in view; pressing one opens it.
 *     Only with more than one photograph.
 *
 * It is controlled: the caller holds `index` and answers onIndex/onClose, so
 * a gallery and a lightbox can each decide what the set is and where to
 * start. Nothing is drawn when `index` names no photograph.
 *
 * photos: PhotoGallery's shape -- [{ s3_url, photo_id?, photo_type?, notes?,
 *         taxon_name?, record_code?, source?, thumbnail_url?, captured_at?,
 *         capture_lat?, capture_lng?, capture_accuracy_m? }]
 */
export default function PhotoViewer({
  photos = [], index = 0, onIndex, onClose, annotatable = false, onAnnotate,
  dialogLabel, renderHead, extraCaption, extraActions, testId = 'photo-viewer', returnFocusTo = null,
  strip = false,
}) {
  const { t, i18n } = useTranslation('common');
  const dialogRef = useRef(null);
  const count = photos.length;
  const open = index !== null && index >= 0 && index < count ? photos[index] : null;
  // A Z-stack's other layer, drawn instead of the photograph (null: the photograph); forgotten at each step.
  const [layerUrl, setLayerUrl] = useState(null);
  useEffect(() => { setLayerUrl(null); }, [index, open?.s3_url]);
  const capture = open ? captureParts(open, i18n?.language) : null;
  const label = (p) => photoTypeLabel(t, p.photo_type);
  // The strip's open photograph, scrolled into view as the reader steps.
  const currentThumb = useRef(null);
  useEffect(() => {
    const el = currentThumb.current;
    if (el && typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [index, strip]);

  // The latest handlers, so the one key listener and the focus return never
  // act on a stale render.
  const latest = useRef({});
  latest.current = { onClose, onIndex, index, count, returnFocusTo };

  const step = (delta) => {
    const { onIndex: go, index: i, count: n } = latest.current;
    if (n > 0 && go) go((i + delta + n) % n);
  };

  useEffect(() => {
    const onKey = (e) => {
      // An arrow in a slider (a Z-stack's layers) is the slider's.
      const inField = e.target?.closest?.('input, select, textarea');
      if (e.key === 'Escape') latest.current.onClose?.();
      else if (inField && (e.key === 'ArrowRight' || e.key === 'ArrowLeft')) return;
      else if (e.key === 'ArrowRight') step(1);
      else if (e.key === 'ArrowLeft') step(-1);
      else trapTab(dialogRef.current, e, { extra: reportTab() });
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // Focus: into the dialog as it opens, back to the opener as it closes.
  useEffect(() => {
    const opener = typeof document !== 'undefined' ? document.activeElement : null;
    dialogRef.current?.focus({ preventScroll: true });
    return () => {
      const back = latest.current.returnFocusTo || opener;
      if (back && typeof back.focus === 'function' && back.isConnected !== false) back.focus({ preventScroll: true });
    };
  }, []);

  if (!open) return null;

  const call = (part) => (typeof part === 'function' ? part(open, index) : part);
  // A photograph with no view (the hub's row picture, before its dossier
  // arrives) is named after the dialog, so the image keeps a name.
  const name = label(open) || dialogLabel || '';

  return (
    <div data-testid={testId} style={overlayStyle} onClick={onClose}>
      <div ref={dialogRef} role="dialog" aria-modal="true" aria-label={dialogLabel || name} tabIndex={-1}
           style={viewerStyle} onClick={e => e.stopPropagation()}>
        <ZoomableImage src={resolvePhotoUrl(layerUrl || open.s3_url)} alt={name}
                       frameStyle={{ borderRadius: '8px 8px 0 0', overflow: 'hidden' }} />
        <div style={viewerCaption}>
          {renderHead ? renderHead(open, index) : (
            <div>
              <strong>{label(open)}</strong>
              {open.taxon_name && <> — <em dir="ltr">{open.taxon_name}</em></>}
              {open.record_code && <span dir="ltr" style={{ opacity: 0.8 }}> · {open.record_code}</span>}
              {open.source === 'reference' && <span style={{ opacity: 0.8 }}> · {t('photoViewer.reference')}</span>}
            </div>
          )}
          {open.notes && <div style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }}>{open.notes}</div>}
          <PhotoCaptureLine capture={capture} t={t} style={{ fontSize: 12, opacity: 0.85, marginTop: 2 }} />
          {open.bundle_id && (
            <BundleLayers bundleId={open.bundle_id} mediaId={open.photo_id || open.image_id} onLayer={setLayerUrl}
                          testId={`${testId}-bundle`} />
          )}
          {call(extraCaption)}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6, gap: 8,
                        flexWrap: 'wrap' }}>
            <span style={{ fontSize: 12 }}>{t('photoViewer.counter', { current: index + 1, total: count })}</span>
            {annotatable && open.photo_id && (
              <button type="button" data-testid="photo-annotate" onClick={() => onAnnotate?.(open)}
                      title={t('tip.photoAnnotate')} style={annotateButton}>
                ✏️ {t('photoViewer.annotate')}
              </button>
            )}
            {call(extraActions)}
            <a href={resolvePhotoUrl(open.s3_url)} target="_blank" rel="noopener noreferrer" title={t('tip.photoOriginal')}
               style={{ color: 'var(--c-primary-border-4-fg)', fontSize: 12 }}>
              {t('photoViewer.openOriginal')}
            </a>
          </div>
          {strip && count > 1 && (
            <div role="group" aria-label={t('photoViewer.strip')} style={stripStyle} data-testid={`${testId}-strip`}>
              {photos.map((p, i) => {
                const on = i === index;
                const words = [t('photoViewer.stripItem', { n: i + 1, total: count }), label(p)].filter(Boolean).join(' · ');
                return (
                  <button key={p.photo_id || p.image_id || i} type="button" ref={on ? currentThumb : undefined}
                          aria-current={on ? 'true' : undefined} aria-label={words} title={words}
                          onClick={() => onIndex?.(i)} style={on ? stripOn : stripThumb}
                          data-testid={`${testId}-strip-${i}`}>
                    <img src={resolvePhotoUrl(p.thumbnail_url || p.s3_url)} alt="" loading="lazy" width={52} height={39}
                         style={stripImage} />
                  </button>
                );
              })}
            </div>
          )}
        </div>
        {count > 1 && (
          <>
            <button type="button" onClick={() => step(-1)} aria-label={t('photoViewer.previous')}
                    title={t('tip.photoPrev')} style={{ ...navButton, insetInlineStart: 8 }}>‹</button>
            <button type="button" onClick={() => step(1)} aria-label={t('photoViewer.next')}
                    title={t('tip.photoNext')} style={{ ...navButton, insetInlineEnd: 8 }}>›</button>
          </>
        )}
        <button type="button" onClick={onClose} aria-label={t('photoViewer.close')} title={t('tip.photoClose')}
                style={closeButton}>×</button>
      </div>
    </div>
  );
}

/**
 * When and where a photo was taken, and how sure that fix was: one line under
 * the photo in a viewer, and nothing at all for a photo with no time and no
 * place. `capture` is utils/photoCapture's captureParts(); `t` may be any
 * namespace's, since the keys name theirs. The map's lightbox (PointDrawer)
 * uses it too. The coordinates are isolated left-to-right, so "lat, lng" keeps
 * its order inside a Hebrew sentence.
 */
export function PhotoCaptureLine({ capture, t, style }) {
  if (!capture || (!capture.when && !capture.where)) return null;
  return (
    <div data-testid="photo-capture" style={style}>
      {capture.when && <span>{t('common:photoViewer.takenAt', { when: capture.when })}</span>}
      {capture.when && capture.where && ' · '}
      {capture.where && (
        <span>
          {t('common:photoViewer.takenWhere')} <bdi dir="ltr">{capture.where}</bdi>
          {capture.accuracy !== null && <> {t('common:photoViewer.accuracy', { m: capture.accuracy })}</>}
        </span>
      )}
    </div>
  );
}

/** The backdrop and the dialog over it: above PhotoGallery's folder (3000). */
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.78)', display: 'flex',
  alignItems: 'center', justifyContent: 'center', zIndex: 3100, padding: 12,
};
const viewerStyle = {
  position: 'relative', maxWidth: '94vw', maxHeight: '94vh', display: 'flex', flexDirection: 'column', outline: 'none',
};
const viewerCaption = {
  background: 'rgba(17,24,39,0.92)', color: 'var(--c-on-accent)', padding: '8px 12px', borderRadius: '0 0 8px 8px', fontSize: 13,
};
const navButton = {
  position: 'absolute', top: '40%', background: 'rgba(0,0,0,0.5)', color: 'var(--c-on-accent)', border: 'none',
  borderRadius: '50%', width: 40, height: 40, fontSize: 26, lineHeight: '36px', cursor: 'pointer', padding: 0,
};
const closeButton = {
  position: 'absolute', top: 8, insetInlineEnd: 8, background: 'rgba(0,0,0,0.55)', color: 'var(--c-on-accent)', border: 'none',
  borderRadius: '50%', width: 30, height: 30, fontSize: 20, cursor: 'pointer', lineHeight: '28px', padding: 0,
};
/** The strip: small copies side by side, scrolled sideways, the open one ringed. */
const stripStyle = {
  display: 'flex', gap: 4, marginTop: 8, paddingBottom: 2, overflowX: 'auto', maxWidth: '90vw',
  overscrollBehaviorX: 'contain',
};
const stripThumb = {
  flex: 'none', padding: 1, background: 'none', border: '2px solid transparent', borderRadius: 5, cursor: 'pointer',
  opacity: 0.75,
};
const stripOn = { ...stripThumb, borderColor: 'var(--c-on-accent)', opacity: 1 };
const stripImage = { display: 'block', width: 52, height: 39, objectFit: 'cover', borderRadius: 3 };
const annotateButton = {
  background: 'rgba(255,255,255,0.15)', color: 'var(--c-on-accent)', border: '1px solid rgba(255,255,255,0.4)',
  borderRadius: 6, padding: '3px 10px', cursor: 'pointer', fontSize: 12,
};
